import { useCallback, useState } from "react";

import { useApolloClient, useMutation } from "@apollo/client";
import { nanoid } from "@reduxjs/toolkit";
import { remove } from "lodash";

import { graphql } from "@/gql";
import { MessageChannel, SendMessageInput } from "@/gql/graphql";
import useDebouncedEffect from "@/hooks/useDebouncedEffect";
import { extractStoreFieldArgs } from "@/utils/apollo";
import { formatISODateTime } from "@/utils/datetime";

const optimisticSenderQuery = graphql(`
  query OptimisticSender {
    coachViewer {
      id
      profile {
        id
        fullName
        avatarUrl(variant: THUMB)
      }
    }
  }
`);

const sendMessageMutation = graphql(`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      message {
        ...ChatMessage
      }
    }
  }
`);

const readMessagesMutation = graphql(`
  mutation ReadMessages($input: ReadMessagesInput!) {
    readMessages(input: $input) {
      messages {
        id
        chatId
        channel
        readAt
      }
    }
  }
`);

export function useSendMessage() {
  const client = useApolloClient();
  const [mutate] = useMutation(sendMessageMutation);

  return useCallback(
    async (input: SendMessageInput) => {
      const { data } = await client.query({
        query: optimisticSenderQuery,
        fetchPolicy: "cache-first",
      });
      const optimisticSender = data!.coachViewer;

      return mutate({
        variables: { input },
        optimisticResponse: {
          __typename: "Mutation",
          sendMessage: {
            __typename: "SendMessagePayload",
            message: {
              __typename: "Message",
              id: `TEMP_${nanoid()}`,
              chatId: input.attributes.chatId ?? "",
              channel: input.attributes.channel ?? MessageChannel.Appointment,
              body: input.attributes.body ?? "",
              attachments: [],
              readAt: null,
              createdAt: formatISODateTime(new Date()),
              deletedAt: null,
              editedAt: null,
              parentMessageId: input.attributes.parentMessageId ?? "",
              parentMessage: null,
              sender: optimisticSender,
              viewerIsSender: true,
            },
          },
        },
        update: (cache, { data }) => {
          const message = data?.sendMessage?.message;
          if (message == null) return;

          cache.modify({
            id: `Chat:${input.attributes.chatId}`,
            fields: {
              latestMessage: (existing, { toReference }) => {
                return toReference(message) ?? existing;
              },
              messages: (previous, { toReference, storeFieldName }) => {
                const args = extractStoreFieldArgs(storeFieldName);
                if (args.channel !== message.channel) return previous;

                return {
                  ...previous,
                  edges: [
                    { __typename: "MessageEdge", node: toReference(message) },
                    ...previous.edges,
                  ],
                };
              },
            },
          });

          //  Put chat to top of chat list
          cache.modify({
            fields: {
              chats: (previous, { storeFieldName }) => {
                const args = extractStoreFieldArgs(storeFieldName);
                if (args.participantName !== "") return previous;

                const nodes = [...previous.nodes];
                const [chat] = remove(
                  nodes,
                  (n) => n.__ref === `Chat:${input.attributes.chatId}`
                );
                return {
                  ...previous,
                  nodes: [chat, ...nodes],
                };
              },
            },
          });
        },
      });
    },
    [mutate]
  );
}

export function useReadMessages(chatId: string, channel: MessageChannel) {
  const [readMessageIds, setReadMessageIds] = useState<string[]>([]);
  const [mutate] = useMutation(readMessagesMutation);

  const setMessageRead = useCallback(
    (messageId: string) => setReadMessageIds((array) => [...array, messageId]),
    []
  );

  useDebouncedEffect(
    () => {
      if (readMessageIds.length === 0) return;

      mutate({
        variables: { input: { ids: readMessageIds } },
        optimisticResponse: {
          __typename: "Mutation",
          readMessages: {
            __typename: "ReadMessagesPayload",
            messages: readMessageIds.map((id) => ({
              id,
              chatId,
              channel,
              __typename: "Message",
              readAt: formatISODateTime(new Date()),
            })),
          },
        },
        update: (cache, { data }) => {
          data?.readMessages?.messages?.forEach((message) => {
            cache.modify({
              id: `Chat:${message.chatId}`,
              fields: {
                viewerUnreadMessagesCount: (previous, { storeFieldName }) => {
                  if (previous == null) return previous;

                  const args = extractStoreFieldArgs(storeFieldName);
                  if (
                    args.channel != null &&
                    args.channel !== message.channel
                  ) {
                    return previous;
                  }

                  return previous - 1;
                },
              },
            });
          });
        },
      }).then(() => setReadMessageIds([]));
    },
    [readMessageIds],
    500
  );

  return setMessageRead;
}
